import { useTranslation } from 'react-i18next';
import { Chip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import type { ProductLookup, WarrantyStatus } from './api';
import type { RowOutcome } from './bulk';

type ChipColor = 'success' | 'warning' | 'danger' | 'default' | 'primary' | 'secondary';

export const WARRANTY_STATUSES: Record<WarrantyStatus, { label: string; color: ChipColor }> = {
  active: { label: 'Active', color: 'success' },
  expired: { label: 'Expired', color: 'default' },
  not_started: { label: 'Not started', color: 'warning' },
  unknown: { label: 'Unknown', color: 'default' },
};

export const warrantyStatus = (status: string | null | undefined) =>
  WARRANTY_STATUSES[status as WarrantyStatus] ?? { label: status || 'Unknown', color: 'default' as const };

export const OUTCOMES: Record<RowOutcome, { label: string; color: ChipColor }> = {
  found: { label: 'Found', color: 'success' },
  not_found: { label: 'Not found', color: 'default' },
  needs_info: { label: 'Needs info', color: 'warning' },
  error: { label: 'Error', color: 'danger' },
};

/** How a check came about (`check.origin`). */
export const ORIGINS: Record<string, string> = {
  lookup: 'Lookup',
  refresh: 'Asked the OEM again',
  bulk_lookup: 'Bulk lookup',
  file_import: 'File import',
};

export const OEM_NAMES: Record<string, string> = {
  hp: 'HP',
  dell: 'Dell',
  lenovo: 'Lenovo',
  dynabook: 'Dynabook',
};

export function localTime(at: string | null | undefined): string {
  if (!at) return '—';
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? at : date.toLocaleString();
}

function sourceHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** Why there is no product, for an answer that has none. */
function MissingUnit({ answer }: { readonly answer: ProductLookup }) {
  const { t } = useTranslation();
  const check = answer.check;
  let tone = 'border-default-200 bg-default-50';
  let text: string;
  if (answer.status === 'not_found') text = t('The OEM has no record of this serial number.');
  else if (answer.status === 'oem_unknown') text = t('The OEM could not be told from this serial number. Pick the OEM and look it up again.');
  else if (answer.status === 'unsupported') text = t('This OEM’s site is not supported yet.');
  else if (answer.status === 'needs_more_info') {
    tone = 'border-warning-200 bg-warning-50';
    const fields = check?.missing_fields ?? [];
    text = fields.length > 0
      ? t('The OEM needs more information: {{fields}}.', { fields: fields.join(', ') })
      : t('The OEM needs more information.');
  } else {
    tone = 'border-danger-200 bg-danger-50';
    const reason = check?.error?.message || check?.error?.code;
    text = reason ? t('The check failed: {{reason}}', { reason }) : t('The check failed.');
  }
  return (
    <div role="status" className={`flex items-center gap-2 rounded-medium border p-3 text-sm ${tone}`}>
      <Icon icon="material-symbols:info-outline" width={18} /> {text}
    </div>
  );
}

/**
 * One unit as the service answers it: product and OEM, the warranty with each coverage, and the
 * check it comes from. Used for a lookup's answer and for a stored product's detail.
 */
export function ProductView({ answer }: { readonly answer: ProductLookup }) {
  const { t } = useTranslation();
  const { product, oem, warranty, check } = answer;
  const shown = warranty ? warrantyStatus(warranty.status) : null;
  const coverages = warranty?.coverages ?? [];

  return (
    <section aria-label={t('Serial {{serial}}', { serial: answer.serial_number })} className="flex flex-col gap-3 rounded-medium border border-default-200 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-foreground">
            {product?.product_name || product?.product_number || answer.serial_number}
          </span>
          <span className="text-xs text-default-500">
            {t('Serial')} <span className="font-mono">{answer.serial_number}</span>
            {product?.product_number ? <> · {t('Product number')} <span className="font-mono">{product.product_number}</span></> : null}
            {product?.model_number ? <> · {t('Model')} <span className="font-mono">{product.model_number}</span></> : null}
            {oem?.name || oem?.code ? <> · {t('OEM')} {oem.name || OEM_NAMES[oem.code ?? ''] || oem.code}</> : null}
          </span>
        </div>
        <Chip size="sm" variant="flat" color={answer.served_from === 'agent' ? 'primary' : 'default'}>
          {answer.served_from === 'agent' ? t('Asked the OEM') : t('From stored data')}
        </Chip>
      </div>

      {answer.stale && (
        <div role="status" className="rounded-medium border border-warning-200 bg-warning-50 p-3 text-sm">
          {t('The OEM could not be asked now, so this is the stored answer.')}
          {answer.refresh_error?.message ? ` ${answer.refresh_error.message}` : ''}
        </div>
      )}

      {!answer.found && <MissingUnit answer={answer} />}

      {answer.found && warranty && shown && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-default-500">{t('Warranty')}</span>
          <Chip size="sm" variant="flat" color={shown.color}>{t(shown.label)}</Chip>
          {warranty.end_date ? <span>{t('ends {{date}}', { date: warranty.end_date })}</span> : null}
          {warranty.status === 'active' && warranty.days_remaining !== null && warranty.days_remaining !== undefined ? (
            <span className="text-default-500">{t('{{days}} days left', { days: warranty.days_remaining })}</span>
          ) : null}
        </div>
      )}

      {coverages.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" aria-label={t('Coverages')}>
            <thead className="text-xs text-default-500">
              <tr>
                <th className="py-1 pr-4 font-medium">{t('Coverage')}</th>
                <th className="py-1 pr-4 font-medium">{t('Type')}</th>
                <th className="py-1 pr-4 font-medium">{t('Service')}</th>
                <th className="py-1 pr-4 font-medium">{t('Start')}</th>
                <th className="py-1 pr-4 font-medium">{t('End')}</th>
                <th className="py-1 font-medium">{t('Status')}</th>
              </tr>
            </thead>
            <tbody>
              {coverages.map((coverage, index) => {
                const status = warrantyStatus(coverage.status);
                return (
                  <tr key={coverage.external_id ?? `${coverage.name}-${index}`} className="border-t border-default-100 align-top">
                    <td className="py-1 pr-4 text-xs">{coverage.name || '—'}</td>
                    <td className="py-1 pr-4 text-xs">{coverage.coverage_type || '—'}</td>
                    <td className="py-1 pr-4 text-xs" title={(coverage.service_levels ?? []).join(', ') || undefined}>
                      {coverage.service_type || '—'}
                    </td>
                    <td className="whitespace-nowrap py-1 pr-4 text-xs">{coverage.start_date || '—'}</td>
                    <td className="whitespace-nowrap py-1 pr-4 text-xs">{coverage.end_date || '—'}</td>
                    <td className="py-1">
                      <Chip size="sm" variant="flat" color={status.color} title={t('The OEM said: {{status}}', { status: coverage.status_reported })}>
                        {t(status.label)}
                      </Chip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {check && (
        <p className="text-xs text-default-500">
          {t('Checked {{when}}', { when: localTime(check.checked_at) })}
          {check.source_url ? (
            <> {t('on')} <a href={check.source_url} target="_blank" rel="noreferrer" className="text-primary underline">{sourceHost(check.source_url)}</a></>
          ) : null}
          {check.origin ? <> · {t(ORIGINS[check.origin] ?? check.origin)}</> : null}
          {answer.next_check_after ? <> · {t('asked again after {{when}}', { when: localTime(answer.next_check_after) })}</> : null}
        </p>
      )}
    </section>
  );
}
